/**
 * Site geneli tarih/saat — tek kaynak.
 * Değiştirmek için .env:
 *   NEXT_PUBLIC_SITE_TIMEZONE=Europe/Istanbul
 *   NEXT_PUBLIC_SITE_LOCALE=tr-TR
 * (TZ yoksa next.config aynı timezone’u process.env.TZ’ye yazar)
 */

function envOr(name: string, fallback: string): string {
  const v = process.env[name]?.trim()
  return v || fallback
}

export const SITE_LOCALE = envOr('NEXT_PUBLIC_SITE_LOCALE', 'tr-TR')

export const SITE_TIMEZONE = envOr(
  'NEXT_PUBLIC_SITE_TIMEZONE',
  envOr('TZ', 'Europe/Istanbul'),
)

export function formatSiteDateTime(
  value: Date | string | number | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (value == null || value === '') return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return '—'
  return date.toLocaleString(SITE_LOCALE, {
    timeZone: SITE_TIMEZONE,
    ...options,
  })
}

export function formatSiteDate(
  value: Date | string | number | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (value == null || value === '') return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return '—'
  return date.toLocaleDateString(SITE_LOCALE, {
    timeZone: SITE_TIMEZONE,
    ...options,
  })
}

export function formatSiteTime(
  value: Date | string | number | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (value == null || value === '') return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return '—'
  return date.toLocaleTimeString(SITE_LOCALE, {
    timeZone: SITE_TIMEZONE,
    ...options,
  })
}

function zonedParts(
  date: Date,
  timeZone: string,
): {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value || 0)
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  }
}

/** Anlık zamanı SITE_TIMEZONE duvar saati olarak datetime-local değeri yapar */
export function toSiteDatetimeLocalValue(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(d.getTime())) return ''
  const p = zonedParts(d, SITE_TIMEZONE)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
}

/**
 * datetime-local string'ini SITE_TIMEZONE duvar saati kabul edip UTC ISO döner.
 * Örn. "2026-08-11T07:01:00" → Istanbul 07:01 → doğru UTC.
 */
export function siteDatetimeLocalToIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(
    String(local || '').trim(),
  )
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  const hour = Number(m[4])
  const minute = Number(m[5])
  const second = Number(m[6] || 0)
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, second)
  for (let i = 0; i < 3; i++) {
    const shown = zonedParts(new Date(utcMs), SITE_TIMEZONE)
    const asUtc = Date.UTC(
      shown.year,
      shown.month - 1,
      shown.day,
      shown.hour,
      shown.minute,
      shown.second,
    )
    const desired = Date.UTC(year, month - 1, day, hour, minute, second)
    const diff = desired - asUtc
    if (diff === 0) break
    utcMs += diff
  }
  return new Date(utcMs).toISOString()
}
